import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useParams, useNavigate, Link } from "react-router-dom";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* =========================== helpers ============================ */
const pad2 = (n) => String(n).padStart(2, "0");
const ymdLocal = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const to24h = (h12, ampm) => {
  const h = Number(h12);
  if (ampm === "AM") return h % 12; // 12AM -> 0
  return (h % 12) + 12; // 12PM -> 12
};

const addDaysYMD = (ymd, days) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));        // do math in UTC
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2,"0")}-${String(dt.getUTCDate()).padStart(2,"0")}`;
};

const decFromHM = (h24, m) => +(h24 + m / 60).toFixed(2);

const minutesFromDateHM = (dateStr /*YYYY-MM-DD*/, h24, m) => {
  // A simple ordinal in minutes: YYYY, MM, DD only (no timezone math)
  const y = Number(dateStr.slice(0, 4));
  const mo = Number(dateStr.slice(5, 7));
  const d = Number(dateStr.slice(8, 10));
  // crude day ordinal (month length differences won’t matter since we only compare two dates we built)
  const dayOrdinal = y * 372 + mo * 31 + d; // consistent, monotonic
  return dayOrdinal * 1440 + h24 * 60 + m;
};

const hhmmPreview = (h24, m) => `${pad2(h24)}:${pad2(m)}`;

/* ======================== component ============================= */
const Vehicle_position_entry = () => {
  useAuthRedirect();

  const { vcode, branch, vehname, vehno } = useParams();
  const navigate = useNavigate();

  const [vehicleDetails, setVehicleDetails] = useState({
    vehicle_type: "",
    vehicle_basis: "",
  });

  const [locations, setLocations] = useState([]);

  // ---- shared form fields ----
  const [formData, setFormData] = useState({
    vehicle_basis: "",
    driver: "",
    location: "",
    // KM basis
    stkm: "",
    endkm: "",
    // Dates (both bases)
    dateLoading: "",
    dateUnloading: "",
    // Loads / diesel
    loads: "",
    dqty: "",
    dconsumption: "",
  });

  // ---- Hour-basis (12h + AM/PM) fields ----
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [startHour12, setStartHour12] = useState("12");
  const [startMinute, setStartMinute] = useState("00");
  const [startAmPm, setStartAmPm] = useState("AM");
  const [endHour12, setEndHour12] = useState("12");
  const [endMinute, setEndMinute] = useState("00");
  const [endAmPm, setEndAmPm] = useState("AM");
  const [endsNextDay, setEndsNextDay] = useState(false);

  const canEntry = hasPermission("Vehicle Daily Progress Entry", "entry");

  /* ---------------------- effects: data ---------------------- */
  useEffect(() => {
    fetchVehicleDetails();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVehicleDetails = async () => {
    try {
      const response = await axios.get(
        "https://rfs2.infraconsoft.net.in/api/vehicle_position_entry",
        {
          params: { vcode },
        }
      );
      if (response.data.vehicle) {
        setVehicleDetails(response.data.vehicle);
        setFormData((prev) => ({
          ...prev,
          vehicle_basis: response.data.vehicle.vehicle_basis,
        }));
      }
    } catch (error) {
      console.error("Error fetching vehicle details:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get("https://rfs2.infraconsoft.net.in/api/locations");
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  /* ---------------------- derived (hour-basis) ---------------------- */
  // keep endDate in sync with "Ends next day" toggle
  useEffect(() => {
  if (endsNextDay) {
    const d = new Date(startDate + "T00:00:00"); // local midnight
    d.setDate(d.getDate() + 1);
    setEndDate(ymdLocal(d));        // ← avoid toISOString()
  } else {
    setEndDate(startDate);
  }
}, [endsNextDay, startDate]);

  const hStart24 = to24h(startHour12, startAmPm);
  const mStart = Number(startMinute);
  const hEnd24 = to24h(endHour12, endAmPm);
  const mEnd = Number(endMinute);

  const startMinutes = minutesFromDateHM(startDate, hStart24, mStart);
  const endMinutes = minutesFromDateHM(endDate, hEnd24, mEnd);
  const durationMinutes = Math.max(0, endMinutes - startMinutes);
  const durationHoursDec = +(durationMinutes / 60).toFixed(2);

  const startDec = decFromHM(hStart24, mStart); // fld_sthrs
  const endDec = decFromHM(hEnd24, mEnd); // fld_endhrs

  const previewWarnings = useMemo(() => {
    const msgs = [];
    if (startDate === endDate && endMinutes < startMinutes) {
      msgs.push(
        "End time is earlier than start time on the same day. Did you mean “Ends next day”?"
      );
    }
    if (durationMinutes === 0) {
      msgs.push(
        "Duration is 0 minutes. If this is an overnight (e.g., 12 AM → next-day 12 AM), turn on “Ends next day”."
      );
    }
    return msgs;
  }, [startDate, endDate, startMinutes, endMinutes, durationMinutes]);

  /* ---------------------- validation helpers ---------------------- */
  const isNonNegativeNumber = (v) =>
    v !== "" && !isNaN(Number(v)) && Number(v) >= 0;
  const isNonNegativeInt = (v) => v !== "" && /^\d+$/.test(String(v));

  /* ---------------------- handlers ---------------------- */
  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // common validations
    if (!formData.driver || formData.driver.trim() === "") {
      Swal.fire("Validation Error", "Please select a driver.", "warning");
      return;
    }
    if (!formData.location || formData.location.trim() === "") {
      Swal.fire("Validation Error", "Please select a location.", "warning");
      return;
    }
    if (!formData.dateLoading || !formData.dateUnloading) {
      Swal.fire(
        "Validation Error",
        "Please select Loading and Unloading dates.",
        "warning"
      );
      return;
    }
    if (!isNonNegativeInt(formData.loads)) {
      Swal.fire(
        "Validation Error",
        "No of Loads must be a non-negative integer.",
        "warning"
      );
      return;
    }
    if (!isNonNegativeNumber(formData.dqty)) {
      Swal.fire(
        "Validation Error",
        "Diesel Qty must be a non-negative number.",
        "warning"
      );
      return;
    }
    // if (!isNonNegativeNumber(formData.dconsumption)) {
    //   Swal.fire(
    //     "Validation Error",
    //     "Diesel Consumption must be a non-negative number.",
    //     "warning"
    //   );
    //   return;
    // }

    let payload = {
      vcode,
      branch,
      vehname,
      vehicle_type: vehicleDetails.vehicle_type,
      vehno,
      vehicle_basis: formData.vehicle_basis,
      driver: formData.driver,
      location: formData.location,
      dateLoading: formData.dateLoading,
      dateUnloading: formData.dateUnloading,
      dqty: formData.dqty,
      dconsumption: formData.dconsumption,
      loads: formData.loads,
    };

    if (formData.vehicle_basis === "Km") {
      // KM validations
      if (formData.stkm === "" || formData.endkm === "") {
        Swal.fire(
          "Validation Error",
          "Starting and Ending KM are required.",
          "warning"
        );
        return;
      }
      const startKm = Number(formData.stkm);
      const endKm = Number(formData.endkm);
      if (isNaN(startKm) || isNaN(endKm)) {
        Swal.fire("Validation Error", "KM values must be numbers.", "warning");
        return;
      }
      if (startKm < 0 || endKm < 0) {
        Swal.fire(
          "Validation Error",
          "KM values cannot be negative.",
          "warning"
        );
        return;
      }
      if (endKm < startKm) {
        Swal.fire(
          "Validation Error",
          "Ending KM cannot be less than Starting KM.",
          "warning"
        );
        return;
      }
      payload = {
        ...payload,
        stkm: startKm,
        endkm: endKm,
        km_worked: endKm - startKm,
      };
    } else {
      // Hour-basis validations
      if (!startDate || !endDate) {
        Swal.fire(
          "Validation Error",
          "Start and End dates are required.",
          "warning"
        );
        return;
      }
      if (startDate === endDate && endMinutes < startMinutes) {
        Swal.fire(
          "Validation Error",
          "End time is earlier than start time. Enable “Ends next day”.",
          "warning"
        );
        return;
      }
      if (durationMinutes <= 0) {
        Swal.fire(
          "Validation Error",
          "Duration is 0. Check your times or toggle “Ends next day”.",
          "warning"
        );
        return;
      }
      // Optionally cap extremely long entries (e.g., 72 hours)
      if (durationMinutes > 72 * 60) {
        Swal.fire(
          "Validation Error",
          "Duration exceeds 72 hours. Please verify.",
          "warning"
        );
        return;
      }

      // Submit legacy fields so your current API stores correct values today
      payload = {
        ...payload,
        // legacy decimal fields
        sthrs: startDec, // e.g., 23.50
        endhrs: endDec, // e.g., 1.25
        work_hrs: durationHoursDec, // e.g., 1.75
        // send 12h primitives too (future-proof server can normalize if you switch)
        hour_basis: {
          startDate,
          endDate,
          startHour12,
          startMinute,
          startAmPm,
          endHour12,
          endMinute,
          endAmPm,
          endsNextDay,
        },
      };
    }

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const resp = await axios.post(
        "https://rfs2.infraconsoft.net.in/api/insert_vehicle_position",
        payload
      );
      // reset
      setFormData((prev) => ({
        vehicle_basis: prev.vehicle_basis,
        driver: "",
        location: "",
        stkm: "",
        endkm: "",
        dateLoading: "",
        dateUnloading: "",
        dqty: "",
        loads: "",
        dconsumption: "",
      }));
      // reset hour inputs (keep dates defaulting to today)
      setStartDate(todayStr);
      setEndDate(todayStr);
      setEndsNextDay(false);
      setStartHour12("12");
      setStartMinute("00");
      setStartAmPm("AM");
      setEndHour12("12");
      setEndMinute("00");
      setEndAmPm("AM");

      Swal.fire({
        title: "Success!",
        text: "Vehicle position data inserted successfully.",
        icon: "success",
        width: "500px",
        height: "40vh",
        draggable: true,
      }).then(() => {
        navigate(
          `/vehicle_working_hours_own/${vcode}/${vehno}/${vehname}/${branch}/${formData.vehicle_basis}`
        );
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Insert Failed",
        text: "Submission failed. Please try again.",
      });
    }
  };

  if (!canEntry) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  /* ---------------------- UI options ---------------------- */
  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => pad2(i));

  /* ======================== render ========================= */
  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Load Entry</h2>
            <Link
              to={`/vehicle_working_hours_own/${vcode}/${vehno}/${vehname}/${branch}/${formData.vehicle_basis}`}
            >
              <p className="back-button">
                <i className="fa-solid fa-backward"></i>Back
              </p>
            </Link>
          </div>
          <div>
            <Time />
          </div>
        </section>

        <div className="form-container">
          <form className="vehicle-form" onSubmit={handleSubmit}>
            {/* header */}
            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Code :</label>
                <input type="text" name="vcode" value={vcode} readOnly />
              </div>
              <div className="form-group">
                <label>Branch Name :</label>
                <input type="text" name="branch" value={branch} readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Name :</label>
                <input type="text" name="vname" value={vehname} readOnly />
              </div>
              <div className="form-group">
                <label>Vehicle Type :</label>
                <input
                  type="text"
                  name="vtype"
                  value={vehicleDetails.vehicle_type}
                  readOnly
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Basis :</label>
                <input
                  type="text"
                  name="vehicle_basis"
                  value={formData.vehicle_basis}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Driver Name :</label>
                <input
                  type="text"
                  name="driver"
                  value={formData.driver}
                  onChange={handleBasicChange}
                  required
                />
              </div>
            </div>

            <h2 className="center-">Enter Load Details</h2>
            <br></br>


  {formData.vehicle_basis === "Hour" && (
  <>
    {previewWarnings.length > 0 && (
  <div className="alert alert--warn" role="status" aria-live="polite">
    <div className="alert__icon" aria-hidden="true">⚠️</div>

    <div className="alert__body">
      {/* header row with title (left) and the toggle (right) */}
      <div className="alert__head">
        <div className="alert__title">Check your time range</div>

        <label className="switch">
          <input
            id="endsNextDay"
            type="checkbox"
            checked={endsNextDay}
            onChange={(e) => setEndsNextDay(e.target.checked)}
          />
          <span className="switch__track" aria-hidden="true"></span>
          <span className="switch__label">Ends next day</span>
        </label>
      </div>

      {/* message content */}
      <div className="alert__msg">
        {previewWarnings.map((w, i) => (
          <div key={i} className="alert__bullet">{w}</div>
        ))}
        <div className="alert__hint">
          Overnight example: <b>12:00 AM → next-day 12:00 AM</b>. If that’s your case, enable
          <em> Ends next day</em>.
        </div>
      </div>
    </div>
  </div>

  
)}

<br></br>

<br></br>
  </>
)}

            {/* basis-specific blocks */}
            {formData.vehicle_basis === "Km" ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Starting km :</label>
                    <input
                      type="text"
                      name="stkm"
                      value={formData.stkm}
                      onChange={handleBasicChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Closing km :</label>
                    <input
                      type="text"
                      name="endkm"
                      value={formData.endkm}
                      onChange={handleBasicChange}
                      required
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date :</label>
                   <input
  type="date"
  value={startDate}
  onChange={(e) => {
    const v = e.target.value;          // "YYYY-MM-DD"
    setStartDate(v);
    setEndDate(endsNextDay && v ? addDaysYMD(v, 1) : v);  // <-- no local Date, no toISOString
  }}
/>
                  </div>
                  <div className="form-group">
                    <label>End Date :</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                  </div>
                </div>


                <div className="form-row">
  <div className="form-group">
    <label>Start Time :</label>
    <div className="time-group field-control">
      <select value={startHour12} onChange={(e) => setStartHour12(e.target.value)}>
        {hourOptions.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="sep">:</span>
      <select value={startMinute} onChange={(e) => setStartMinute(e.target.value)}>
        {minuteOptions.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <span className="sep"> </span>
      <select value={startAmPm} onChange={(e) => setStartAmPm(e.target.value)}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  </div>

  <div className="form-group">
    <label>End Time :</label>
    <div className="time-group field-control">
      <select value={endHour12} onChange={(e) => setEndHour12(e.target.value)}>
        {hourOptions.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="sep">:</span>
      <select value={endMinute} onChange={(e) => setEndMinute(e.target.value)}>
        {minuteOptions.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <span className="sep"> </span>
      <select value={endAmPm} onChange={(e) => setEndAmPm(e.target.value)}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  </div>
</div>

  {/* <small className="hint">12:00 AM = 00:00, 12:00 PM = 12:00</small> */}


{/* <div className="form-row">
  <div className="form-group">
    <label>Preview (24h) :</label>
<input
  type="text"
  value={`Start ${formatDMY(startDate)} → End ${formatDMY(endDate)}${endDate !== startDate ? " (crosses day)" : ""}`}
  readOnly
/>
  </div>
  <div className="form-group">
    <label>Working Hours :</label>
    <input
      type="text"
      value={`${durationHoursDec.toFixed(2)} hours`}
      readOnly
    />
  </div>
</div> */}


              </>
            )}

            {/* shared fields */}
            <div className="form-row">
              <div className="form-group">
                <label>Date of Loading :</label>
                <input
                  type="date"
                  name="dateLoading"
                  value={formData.dateLoading}
                  onChange={handleBasicChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Date of UnLoading :</label>
                <input
                  type="date"
                  name="dateUnloading"
                  value={formData.dateUnloading}
                  onChange={handleBasicChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Location :</label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleBasicChange}
                  required
                >
                  <option value="">-- Select Location --</option>
                  {locations.map((l, idx) => (
                    <option key={idx} value={l.fld_location}>
                      {l.fld_location}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>No of Loads :</label>
                <input
                  type="number"
                  name="loads"
                  min="0"
                  step="1"
                  value={formData.loads}
                  onChange={handleBasicChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Diesel Qty :</label>
                <input
                  type="number"
                  name="dqty"
                  min="0"
                  step="any"
                  value={formData.dqty}
                  onChange={handleBasicChange}
                  required
                />
              </div>
              <div className="form-group">
                <label></label>
                <input
                  type="hidden"
                  name="dconsumption"
                  min="0"
                  step="any"
                  value={formData.dconsumption}
                  onChange={handleBasicChange}
                  

                />
              </div>
            </div>

            <div className="btn-cls">
              <button type="submit" className="submit-btn">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Vehicle_position_entry;
